import { useCallback, useEffect, useState } from 'react'
import {
	FileContainer,
	fileSelectors,
	fileSlice,
	FileStatuses,
	FileWithPreview,
	prepareFileDescr,
} from 'Modules'
import { useAppDispatch } from 'Src/hooks'
import { useSelector } from 'react-redux'
import { formatDateForNewUpload, getActualSize } from 'Helpers'
import { FieldNames } from 'Constants/FieldNames'

export const useFileApi = (fieldName: FieldNames) => {
	const [error, setError] = useState<string>('')
	const { setFileContainersList } = useAppDispatch({
		setFileContainersList: fileSlice.actions.setFileContainersList,
	})

	const filesBlob = useSelector(fileSelectors.getFilesBlob)
	const fileContainerMap = useSelector(fileSelectors.getFileContainersMap)
	const fileContainersList = fileContainerMap.get(fieldName) || []

	const clearFileContainersList = useCallback(() => {
		setFileContainersList({ fieldName, fileContainersList: [] })
	}, [fieldName])

	useEffect(() => {
		setFileContainersList({
			fieldName,
			fileContainersList: fileContainersList?.map(({ file, ...args }) => {
				if (file && !file.preview) {
					const url = file.file
					const blobFile = filesBlob.get(url)
					if (blobFile) {
						file = { ...file, preview: window.URL.createObjectURL(blobFile) }
					}
				}
				return { file, ...args }
			}),
		})
	}, [filesBlob.size])

	const changeFileContainerStatusAndDescr = (
		fileName: string[],
		status: FileStatuses,
		description?: string
	) => {
		setFileContainersList({
			fieldName,
			fileContainersList: fileContainersList?.map((fileContainer): FileContainer => {
				if (fileName.includes(fileContainer.file.name)) {
					return { ...fileContainer, description, status }
				}
				return fileContainer
			}),
		})
	}

	const deleteFileContainer = (fileName: string) => {
		const filtredFileContainersList = fileContainersList?.filter(
			(fileContainer) => fileName !== fileContainer.file.name
		)
		setFileContainersList({
			fieldName,
			fileContainersList: filtredFileContainersList,
		})
		return filtredFileContainersList
	}

	const getFileList = fileContainersList?.map(({ file }) => file)

	const formatForFileContainer = (rawFileArray: FileWithPreview[]): FileContainer[] =>
		rawFileArray.map(
			(elem) =>
				({
					file: elem,
					status: FileStatuses.COMPLETE,
					description: prepareFileDescr([
						getActualSize(elem?.size),
						formatDateForNewUpload(elem?.created_at ?? new Date().toString()),
					]),
				} as FileContainer)
		)

	return {
		fileContainersList,
		setFileContainersList,
		clearFileContainersList,
		deleteFileContainer,
		changeFileContainerStatusAndDescr,
		getFileList,
		error,
		setError,
		formatForFileContainer,
	}
}
